import { Fab } from '@mui/material';
import { useSearchkit } from '@searchkit/client';
import React, { useCallback, useMemo } from 'react';
import { FacetsList } from '../../../../../supply/searchkit/facets/FacetsList';
import FacetsPanel from '../../../../../supply/searchkit/facets/FacetsPanel';
import type { QuickFilter } from '../../../../components/filtering/QuickFilterItem';
import QuickFilterList from '../../../../components/filtering/QuickFilterList';
import { SIZES } from '../../../../constants/constants';
import { useI18n } from '../../../../i18n/I18n';
import { Icons } from '../../../../icons/Icons';
import { H } from '../../../../Layout';
import type { WorkEntryRow } from '../../../types';

interface WorkEntriesHeaderProps<TSource> {
    rows: ReadonlyArray<WorkEntryRow<TSource>>;
    enableSearch: boolean;
    enableFilters: boolean;
    enableKeyQuickFilter?: boolean;
    keyFilterValues?: string[];
    onKeyFilterChange?: (values: string[]) => void;
    keyQuickFilterTooltip?: string;
    compactHorizontalPadding?: boolean;
    onSearchQueryChange: (query: string) => void;
    inlineAddButton?: { onAdd: () => void };
}

export default function WorkEntriesHeader<TSource>({
    rows,
    enableSearch,
    enableFilters,
    enableKeyQuickFilter = false,
    keyFilterValues = [],
    onKeyFilterChange,
    keyQuickFilterTooltip,
    compactHorizontalPadding = false,
    onSearchQueryChange,
    inlineAddButton
}: WorkEntriesHeaderProps<TSource>) {
    const api = useSearchkit();
    const t = useI18n();

    const { typeCounts, yearCounts } = useMemo(() => {
        const typeCounts = new Map<string, number>();
        const yearCounts = new Map<number, number>();
        rows.forEach((row) => {
            if (row.typeValue) {
                typeCounts.set(row.typeValue, (typeCounts.get(row.typeValue) ?? 0) + 1);
            }
            row.years.forEach((year) => {
                yearCounts.set(year, (yearCounts.get(year) ?? 0) + 1);
            });
        });
        return { typeCounts, yearCounts };
    }, [rows]);

    const typeOptions = useMemo(() => [...typeCounts.keys()].sort((a, b) => a.localeCompare(b)), [typeCounts]);
    const yearOptions = useMemo(() => [...yearCounts.keys()].sort((a, b) => b - a), [yearCounts]);

    const keyQuickFilter = useMemo<QuickFilter>(
        () =>
            ({
                id: 'keyEntry',
                label: (
                    <H>
                        <Icons.ComedyMask fontSize={SIZES.medium} />
                    </H>
                ),
                options: [t('workEntries.filter.key.true'), t('workEntries.filter.key.false')],
                values: keyFilterValues,
                tooltip: keyQuickFilterTooltip
            }) as unknown as QuickFilter,
        [keyFilterValues, keyQuickFilterTooltip, t]
    );

    const handleKeyQuickFilterChanged = useCallback(
        (changed: QuickFilter) => {
            onKeyFilterChange?.([...(changed.values ?? [])]);
        },
        [onKeyFilterChange]
    );

    const facetsData = useMemo(
        () => ({
            results: {
                hits: { page: { total: rows.length } },
                facets: [
                    {
                        identifier: 'type',
                        label: t('workEntries.filter.type'),
                        display: 'CustomListFacet',
                        entries: typeOptions.map((type) => ({
                            id: `type_${type}`,
                            label: type,
                            count: typeCounts.get(type) ?? 0
                        }))
                    },
                    {
                        identifier: 'year',
                        label: t('workEntries.filter.year'),
                        display: 'CustomListFacet',
                        entries: yearOptions.map((year) => ({
                            id: `year_${year}`,
                            label: String(year),
                            count: yearCounts.get(year) ?? 0
                        }))
                    }
                ]
            }
        }),
        [rows.length, typeOptions, typeCounts, yearOptions, yearCounts, t]
    );

    const FacetsRenderer = useMemo(() => FacetsList([]), []);

    const handleSearch = useCallback(
        (query: string) => {
            onSearchQueryChange(query);
            api.setSearchState({
                ...api.searchState,
                query
            });
        },
        [api, onSearchQueryChange]
    );

    if (!enableSearch && !enableFilters && !enableKeyQuickFilter) return null;

    return (
        <H
            sx={{
                width: '100%',
                alignItems: 'center',
                gap: 1.5,
                px: compactHorizontalPadding ? 0 : 2,
                py: 1,
                ...(inlineAddButton ? { position: 'sticky', top: 0, zIndex: 1 } : {}),
                '& ._facetsPanel': {
                    flex: 1,
                    minWidth: 0,
                    justifyContent: 'flex-start',
                    gap: 1.5,
                    '& > :last-child': {
                        flex: 1,
                        minWidth: 0
                    }
                }
            }}
        >
            {inlineAddButton && (
                <Fab
                    sx={{
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        minHeight: 32,
                        boxShadow: 3
                    }}
                    size="small"
                    color="primary"
                    aria-label="add"
                    variant="circular"
                    onClick={inlineAddButton?.onAdd}
                >
                    <Icons.AddOutlined />
                </Fab>
            )}
            <FacetsPanel
                id="work-entries-list-facets"
                Facets={FacetsRenderer}
                data={facetsData as any}
                loading={false}
                searching={false}
                onSearch={enableSearch ? handleSearch : undefined}
                searchPlaceholder={t('notes.search')}
                isLocal
                showCount={false}
                hideAllFilterLabel
                facetsVisibility={{
                    filters: enableFilters,
                    searchPanel: enableSearch,
                    promotedFacets: false,
                    quickFilter: false
                }}
            />
            {enableKeyQuickFilter && onKeyFilterChange ? (
                <QuickFilterList filters={[keyQuickFilter]} onFilterChanged={handleKeyQuickFilterChanged} />
            ) : null}
        </H>
    );
}
